import classnames from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { Link } from '@eika/link';

import { useConfig } from '../../../defaultConfig';
import useTranslation from '../../../hooks/useTranslation';

import './NavigationButtons.scss';

interface Props {
    nextUrl?: string;
    onClick?: () => boolean | Promise<boolean>;
    paymentButtonText?: string;
    paymentButton?: boolean;
    cancelUrl?: string;
    paymentOperationPending?: boolean;
    showNextButton?: boolean;
    centerOnSmallerWidth?: boolean;
    className?: string;
    hidePrevButton?: boolean;
    mainButtonIsLoading?: boolean;
    dataTrackId?: string;
}

const NavigationButtons: React.FC<Props> = ({
    nextUrl,
    onClick,
    paymentButtonText,
    paymentButton = false,
    cancelUrl,
    paymentOperationPending,
    showNextButton = true,
    centerOnSmallerWidth = false,
    className,
    hidePrevButton = false,
    mainButtonIsLoading = false,
    dataTrackId,
}) => {
    const translated = useTranslation();
    const { cancelCrossBorderPaymentLink } = useConfig();
    const navigate = useNavigate();

    return (
        <div className={classnames('navigation-buttons', { 'smaller-width': centerOnSmallerWidth }, className)}>
            <div className="navigation-buttons__first-row">
                {!hidePrevButton ? (
                    <StandardButton
                        className="navigation-buttons__previous-button"
                        buttonType="negative"
                        onClick={() => navigate(-1)}
                    >
                        {translated.previous}
                    </StandardButton>
                ) : (
                    <Link
                        href={cancelUrl ?? cancelCrossBorderPaymentLink?.url}
                        className="navigation-buttons__cancel-button navigation-buttons__previous-button"
                    >
                        {translated.cancel}
                    </Link>
                )}

                {paymentButton && showNextButton ? (
                    <ButtonWithSpinner
                        className="navigation-buttons__next-button"
                        buttonType="positive"
                        type="submit"
                        showSpinner={mainButtonIsLoading || paymentOperationPending}
                        data-track-id={dataTrackId}
                        data-track-linkname="navigation__submit"
                    >
                        {paymentButtonText ?? translated.payWithBankId}
                    </ButtonWithSpinner>
                ) : (
                    <>
                        {showNextButton && (
                            <ButtonWithSpinner
                                buttonType="positive"
                                className="navigation-buttons__next-button"
                                onClick={async () => {
                                    const navigation = await onClick?.();
                                    if (navigation || navigation === undefined) {
                                        navigate(nextUrl?.startsWith('/') ? nextUrl : `/${nextUrl}`);
                                    }
                                }}
                                showSpinner={mainButtonIsLoading}
                            >
                                {translated.next}
                            </ButtonWithSpinner>
                        )}
                    </>
                )}
            </div>
            {!hidePrevButton && (
                <Link
                    href={cancelUrl ?? cancelCrossBorderPaymentLink?.url}
                    className="navigation-buttons__cancel-button"
                >
                    {translated.cancel}
                </Link>
            )}
        </div>
    );
};

export default NavigationButtons;
